import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getNicheMeta } from "../components/niche/nicheMeta";
import { NicheIcon } from "../components/niche";
import { checkFollowing, toggleFollow } from "../services/followService";
import { formatCount } from "../utils/formatCount";
import { getCreatorProfilePathFromHandle, getUserProfilePath } from "../utils/profilePaths";
import {
  addRecentSearch,
  buildTrendingSearches,
  clearRecentSearches,
  getNicheBrowseStats,
  getRecentSearches,
  getSearchCatalog,
  removeRecentSearch,
  searchCatalog,
} from "../services/searchService";
import "./Search.css";

const STROKE = 2;

const NICHE_EMOJI = {
  wellness: "🌿",
  beauty: "✨",
  food: "🍳",
  fitness: "💪",
  auto: "🔧",
  mind: "🧠",
  arts: "🎨",
  independent: "✦",
};

const NICHE_PILL_COLORS = {
  wellness: { bg: "#E4F2ED", color: "#4A7A5C" },
  beauty: { bg: "#F4EEF8", color: "#6B4FA8" },
  food: { bg: "#FDF3E0", color: "#9A6B12" },
  fitness: { bg: "#EEF3F8", color: "#3A5A7A" },
  auto: { bg: "#F4EEE8", color: "#506070" },
  mind: { bg: "#F0EEF8", color: "#5A4890" },
  arts: { bg: "#F8EEF0", color: "#9B3A3A" },
  independent: { bg: "#EEF4EE", color: "#5C5348" },
};

function formatDropPrice(drop) {
  if (drop.accessType === "free" || !drop.price || Number(drop.price) === 0) return "Free";
  return `$${Number(drop.price).toFixed(2)}`;
}

function dropResultPath(drop, signedIn) {
  if (signedIn) return `/drop/${drop.id}`;
  const creator = drop.creator || {};
  return (
    getUserProfilePath({
      ...creator,
      uid: creator.uid || drop.creatorId,
    }) ||
    getCreatorProfilePathFromHandle(
      creator.handle || creator.username,
      drop.creatorId
    ) ||
    "/search"
  );
}

function dropTimeLabel(drop) {
  const ts = drop.createdAt?.toDate?.() || drop.publishedAt?.toDate?.();
  if (!ts) return "";
  const days = Math.floor((Date.now() - ts.getTime()) / 86400000);
  if (days === 0) return "Today";
  if (days === 1) return "1 day ago";
  if (days < 7) return `${days} days ago`;
  if (days < 14) return "1 week ago";
  return `${Math.floor(days / 7)} weeks ago`;
}

function ChevronRight() {
  return (
    <svg className="sp-niche-tile-arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={STROKE} strokeLinecap="round" aria-hidden>
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

function TrendArrow() {
  return (
    <svg className="sp-trending-arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={STROKE} strokeLinecap="round" aria-hidden>
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

export default function Search() {
  const { currentUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const inputRef = useRef(null);

  const [catalog, setCatalog] = useState(null);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [query, setQuery] = useState(searchParams.get("q") || "");
  const [activeTab, setActiveTab] = useState("all");
  const [recentSearches, setRecentSearches] = useState(getRecentSearches);
  const [followingMap, setFollowingMap] = useState({});
  const [followLoading, setFollowLoading] = useState({});

  useEffect(() => {
    getSearchCatalog()
      .then(setCatalog)
      .catch(console.error)
      .finally(() => setCatalogLoading(false));
  }, []);

  useEffect(() => {
    const q = searchParams.get("q") || "";
    setQuery(q);
  }, [searchParams]);

  useEffect(() => {
    function onKeyDown(e) {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const trimmedQuery = query.trim();
  const hasQuery = trimmedQuery.length > 0;

  const results = useMemo(() => {
    if (!hasQuery || !catalog) return { creators: [], drops: [] };
    return searchCatalog(catalog, trimmedQuery);
  }, [catalog, trimmedQuery, hasQuery]);

  const nicheStats = useMemo(
    () => (catalog ? getNicheBrowseStats(catalog) : []),
    [catalog]
  );

  const trending = useMemo(
    () => (catalog ? buildTrendingSearches(catalog) : []),
    [catalog]
  );

  useEffect(() => {
    if (!currentUser || !catalog || results.creators.length === 0) return;
    results.creators.forEach((c) => {
      checkFollowing({ followerId: currentUser.uid, creatorId: c.uid }).then((isFollowing) => {
        setFollowingMap((prev) => ({ ...prev, [c.uid]: isFollowing }));
      });
    });
  }, [currentUser, catalog, results.creators]);

  const applyQuery = useCallback(
    (term, { saveRecent = true } = {}) => {
      const cleaned = term.trim();
      setQuery(cleaned);
      if (cleaned) {
        setSearchParams({ q: cleaned }, { replace: true });
        if (saveRecent) setRecentSearches(addRecentSearch(cleaned));
      } else {
        setSearchParams({}, { replace: true });
      }
      setActiveTab("all");
    },
    [setSearchParams]
  );

  const handleFollow = async (e, creator) => {
    e.preventDefault();
    e.stopPropagation();
    if (!currentUser || followLoading[creator.uid]) return;
    setFollowLoading((prev) => ({ ...prev, [creator.uid]: true }));
    try {
      const handle = creator.handle || creator.username || "";
      const next = await toggleFollow({
        followerId: currentUser.uid,
        creatorId: creator.uid,
        isFollowing: followingMap[creator.uid],
        creatorUsername: handle,
        creatorDisplayName: creator.displayName,
        creatorAvatar: creator.avatar || creator.avatarUrl,
      });
      setFollowingMap((prev) => ({ ...prev, [creator.uid]: next }));
    } catch (err) {
      console.error("Follow error:", err);
    } finally {
      setFollowLoading((prev) => ({ ...prev, [creator.uid]: false }));
    }
  };

  const visibleCreators =
    activeTab === "drops" ? [] : results.creators;
  const visibleDrops =
    activeTab === "creators" ? [] : results.drops;
  const totalResults = results.creators.length + results.drops.length;

  const showCreators = activeTab === "all" || activeTab === "creators";
  const showDrops = activeTab === "all" || activeTab === "drops";

  return (
    <div className="sp-page">
      <div className="sp-hero sp-anim-fade-up">
        <span className="sp-eyebrow">Find anything</span>
        <h1 className="sp-headline">
          Who are you
          <br />
          <em>looking for?</em>
        </h1>
        <span className="sp-tagline">type a name, a niche, or a drop title</span>

        <div className="sp-input-wrap">
          <svg className="sp-input-icon" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={STROKE} strokeLinecap="round" aria-hidden>
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            ref={inputRef}
            className="sp-input"
            type="search"
            placeholder="Search creators, drops, niches..."
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActiveTab("all");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && trimmedQuery) {
                applyQuery(trimmedQuery, { saveRecent: true });
              }
            }}
            aria-label="Search creators and drops"
            autoComplete="off"
          />
          <div className="sp-kbd">⌘K</div>
        </div>

        <div className="sp-purpose sp-anim-fade-up-1">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden>
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <strong>Search</strong> finds specific people and drops · <strong>Gallery</strong> is for browsing and discovering
        </div>
      </div>

      {!hasQuery && (
        <>
          <section className="sp-niche-section sp-anim-fade-up-2" aria-label="Browse by niche">
            <div className="sp-section-label">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={STROKE} strokeLinecap="round" aria-hidden>
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              Browse by niche
            </div>
            <div className="sp-niche-grid">
              {nicheStats.map((niche) => (
                <button
                  key={niche.key}
                  type="button"
                  className={`sp-niche-tile sp-niche-tile--${niche.key}`}
                  onClick={() => applyQuery(niche.label)}
                >
                  <div className="sp-niche-tile-icon">{NICHE_EMOJI[niche.key]}</div>
                  <div className="sp-niche-tile-name">{niche.label}</div>
                  <div className="sp-niche-tile-count">
                    {niche.creatorCount === 0
                      ? "0 creators · coming soon"
                      : `${niche.creatorCount} creator${niche.creatorCount === 1 ? "" : "s"} · ${niche.openDrops} open`}
                  </div>
                  <ChevronRight />
                </button>
              ))}
            </div>
          </section>

          <div className="sp-two-col sp-anim-fade-up-3">
            <section className="sp-trending-section" aria-label="Trending searches">
              <div className="sp-col-label">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={STROKE} strokeLinecap="round" aria-hidden>
                  <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
                  <polyline points="17 6 23 6 23 12" />
                </svg>
                Trending searches
              </div>
              <div className="sp-trending-list">
                {trending.map((item, i) => (
                  <button
                    key={`${item.text}-${i}`}
                    type="button"
                    className="sp-trending-item"
                    onClick={() => applyQuery(item.query || item.text)}
                  >
                    <span className={`sp-trending-rank${i < 2 ? " hot" : ""}`}>{i + 1}</span>
                    <div className="sp-trending-body">
                      <span className="sp-trending-text">{item.text}</span>
                      <div className="sp-trending-meta">
                        <span className={`sp-trending-badge ${item.type}`}>{item.meta}</span>
                      </div>
                    </div>
                    <TrendArrow />
                  </button>
                ))}
              </div>
            </section>

            <section className="sp-recent-section" aria-label="Recent searches">
              <div className="sp-col-label sp-col-label--split">
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={STROKE} strokeLinecap="round" aria-hidden>
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                  Your recent searches
                </div>
                {recentSearches.length > 0 && (
                  <button
                    type="button"
                    className="sp-recent-clear"
                    onClick={() => setRecentSearches(clearRecentSearches())}
                  >
                    Clear all
                  </button>
                )}
              </div>
              <div className="sp-recent-list">
                {recentSearches.length === 0 ? (
                  <p className="sp-trending-meta" style={{ padding: "10px 14px" }}>
                    Your recent searches will appear here.
                  </p>
                ) : (
                  recentSearches.map((term) => (
                    <div key={term} className="sp-recent-row">
                      <button
                        type="button"
                        className="sp-recent-item"
                        onClick={() => applyQuery(term)}
                      >
                        <div className="sp-recent-icon">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9A9088" strokeWidth={STROKE} strokeLinecap="round" aria-hidden>
                            <circle cx="11" cy="11" r="8" />
                            <path d="M21 21l-4.35-4.35" />
                          </svg>
                        </div>
                        <span className="sp-recent-text">{term}</span>
                      </button>
                      <button
                        type="button"
                        className="sp-recent-clear"
                        aria-label={`Remove ${term}`}
                        onClick={() => setRecentSearches(removeRecentSearch(term))}
                      >
                        ×
                      </button>
                    </div>
                  ))
                )}
              </div>
            </section>
          </div>
        </>
      )}

      {hasQuery && (
        <div className="sp-anim-fade-up">
          {catalogLoading ? (
            <div className="sp-loading">Searching…</div>
          ) : (
            <>
              <div className="sp-results-header">
                <div className="sp-results-query">
                  Results for <em>&quot;{trimmedQuery}&quot;</em>
                </div>
                <div className="sp-results-count">
                  {totalResults} result{totalResults === 1 ? "" : "s"}
                </div>
              </div>

              <div className="sp-results-tabs" role="tablist">
                {[
                  { id: "all", label: `All (${totalResults})` },
                  { id: "creators", label: `Creators (${results.creators.length})` },
                  { id: "drops", label: `Drops (${results.drops.length})` },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={activeTab === tab.id}
                    className={`sp-results-tab${activeTab === tab.id ? " active" : ""}`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {totalResults === 0 ? (
                <div className="sp-no-results">
                  <h3>No matches found</h3>
                  <p>Try a creator name, drop title, or niche like Wellness or Beauty.</p>
                  <span className="sp-no-results-hand">or browse the gallery to discover something new →</span>
                </div>
              ) : (
                <>
                  {showCreators && visibleCreators.length > 0 && (
                    <>
                      <div className="sp-result-group-label">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={STROKE} strokeLinecap="round" aria-hidden>
                          <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                          <circle cx="12" cy="7" r="4" />
                        </svg>
                        Creators
                      </div>
                      {visibleCreators.map((creator) => {
                        const handle = creator.handle || creator.username || creator.uid;
                        const nicheMeta = getNicheMeta(creator.niche);
                        const pill = NICHE_PILL_COLORS[nicheMeta.key] || NICHE_PILL_COLORS.independent;
                        const location = creator.location ? ` · ${creator.location}` : "";

                        return (
                          <Link
                            key={creator.uid}
                            to={`/creator/${handle}`}
                            className="sp-creator-result"
                            onClick={() => addRecentSearch(trimmedQuery)}
                          >
                            <img
                              className="sp-cr-avatar"
                              src={creator.avatar || creator.avatarUrl || "https://i.pravatar.cc/80"}
                              alt=""
                            />
                            <div className="sp-cr-info">
                              <div className="sp-cr-name">{creator.displayName || handle}</div>
                              <div className="sp-cr-handle">
                                @{handle}
                                {location}
                              </div>
                              <div className="sp-cr-meta">
                                <span
                                  className="sp-cr-niche"
                                  style={{ background: pill.bg, color: pill.color }}
                                >
                                  <NicheIcon niche={nicheMeta.key} size={10} />
                                  {nicheMeta.label}
                                </span>
                                <span className="sp-cr-followers">
                                  {formatCount(creator.followerCount || creator.subscriberCount || 0)} followers
                                </span>
                                {creator.openDropCount > 0 && (
                                  <span className="sp-cr-open">
                                    <span className="sp-cr-open-dot" />
                                    {creator.openDropCount} drop{creator.openDropCount === 1 ? "" : "s"} open
                                  </span>
                                )}
                              </div>
                            </div>
                            {currentUser && currentUser.uid !== creator.uid && (
                              <button
                                type="button"
                                className={`sp-cr-follow${followingMap[creator.uid] ? " active" : ""}`}
                                onClick={(e) => handleFollow(e, creator)}
                                disabled={followLoading[creator.uid]}
                              >
                                {followingMap[creator.uid] ? "Following ✓" : "Follow"}
                              </button>
                            )}
                          </Link>
                        );
                      })}
                    </>
                  )}

                  {showCreators && showDrops && visibleCreators.length > 0 && visibleDrops.length > 0 && (
                    <div className="sp-divider" />
                  )}

                  {showDrops && visibleDrops.length > 0 && (
                    <>
                      <div className="sp-result-group-label">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={STROKE} strokeLinecap="round" aria-hidden>
                          <rect x="3" y="11" width="18" height="11" rx="2" />
                          <path d="M7 11V7a5 5 0 0110 0v4" />
                        </svg>
                        Drops
                      </div>
                      {visibleDrops.map((drop) => {
                        const creatorName =
                          drop.creator?.displayName || drop.creatorDisplayName || "Creator";
                        const isFree = drop.accessType === "free" || !drop.price || Number(drop.price) === 0;
                        const urgent = !drop.closed && drop.viewsLeft <= 15;
                        const timeLabel = dropTimeLabel(drop);

                        return (
                          <Link
                            key={drop.id}
                            to={dropResultPath(drop, !!currentUser)}
                            className={`sp-drop-result${drop.closed ? " closed" : ""}`}
                            onClick={() => addRecentSearch(trimmedQuery)}
                          >
                            <img
                              className="sp-dr-thumb"
                              src={drop.thumbnailUrl || drop.thumbnail || drop.mediaUrl || "https://images.unsplash.com/photo-1616394584738-fc6e612e71b9?w=200&q=80"}
                              alt=""
                            />
                            <div className="sp-dr-info">
                              <div className="sp-dr-title">{drop.title || "Untitled drop"}</div>
                              <div className="sp-dr-creator">
                                {creatorName}
                                {timeLabel && ` · ${timeLabel}`}
                                {drop.closed && " · Closed"}
                              </div>
                              <div className="sp-dr-meta">
                                <span className={`sp-dr-views${urgent ? " closing" : ""}`}>
                                  {drop.closed
                                    ? `${drop.viewCount || 0}/${drop.maxViews || 100} · Closed`
                                    : `${drop.viewCount || 0}/${drop.maxViews || 100}`}
                                </span>
                                <div className="sp-dr-prog">
                                  <div
                                    className={`sp-dr-prog-fill${urgent ? " closing" : ""}${drop.closed ? " closed" : ""}`}
                                    style={{ width: `${drop.progressPct}%` }}
                                  />
                                </div>
                                {!drop.closed && (
                                  <span className="sp-dr-price">{formatDropPrice(drop)}</span>
                                )}
                              </div>
                            </div>
                            {drop.closed ? (
                              <span className="sp-dr-gone">Gone forever</span>
                            ) : (
                              <span className={`sp-dr-unlock${isFree ? " free" : ""}`}>
                                {isFree ? "Watch Free" : "Unlock"}
                              </span>
                            )}
                          </Link>
                        );
                      })}
                    </>
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
